import { useEffect, useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { Controller, useForm, useFormState } from 'react-hook-form';

import { Message } from '@components';
import {
  DateField,
  DateTimeField,
  FormError,
  FormFieldset,
  ImageField,
  MultiSelectField,
  SimpleHTMLField,
  TextField,
  TextAreaField,
  ToggleField,
} from '@Forms/fields.jsx';
import { errorsFromMutate } from '@Forms/utils.js';
import { ButtonSpinnerIcon } from '@svgs';

const AuthorForm = ({ authorData, disabled, mutation, onCancel, siteID }) => {
  const useFormReturn = useForm({
    defaultValues: authorData,
    mode: 'onBlur',
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setError,
    control,
    reset,
    resetField,
    setFocus,
    setValue,
    trigger,
    watch,
  } = useFormReturn;
  const { isDirty, isValid } = useFormState({ control });

  // handling for tinymce fields
  const editorRefs = useRef({});

  useEffect(() => {
    // set focus to the name field if this is a new author
    if (!authorData.id) {
      setFocus('name');
    }
  }, [setFocus]);

  const onSubmit = (data) => {
    // update the image in the case it has been cleared in
    // the image field
    data = {
      ...data,
      image: data.image.id ? data.image : null,
    };
    mutation.mutate(data, {
      onError: (error) => {
        // handle server-side validations and errors
        const errorsList = errorsFromMutate(error);
        errorsList.forEach(({ field, error }) => setError(field, error));
      },
    });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={
        `sb-manage-content-form ` + (disabled ? 'sb-form-disabled' : '')
      }
    >
      <FormFieldset>
        <TextField
          register={register}
          error={errors.name}
          options={{ required: 'This field is required' }}
          label="Author name"
          name="name"
        />
        <TextField
          register={register}
          error={errors.title}
          label="Author title"
          name="title"
        />
      </FormFieldset>
      <FormFieldset legend="Author image">
        <ImageField
          useFormReturn={useFormReturn}
          label="Author image"
          name="image"
          siteID={siteID}
        />
        <TextField
          register={register}
          error={errors.image_alt}
          name="image_alt"
          label="Image alt"
          helpText="Set alternate text for the author image. Defaults to the author name if left blank"
          placeholder={watch('name')}
        />
      </FormFieldset>
      <FormFieldset legend="Author biography">
        <SimpleHTMLField
          register={register}
          control={control}
          editorRefs={editorRefs}
          initialValue={authorData.bio}
          error={errors.bio}
          name="bio"
          label="Biography"
        />
      </FormFieldset>
      <div className="sb-manage-content-button-bar">
        <div className="sb-flex">
          {onCancel && (
            <button
              className="sb-btn-cancel sb-btn"
              onClick={onCancel}
              disabled={disabled}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="sb-btn sb-btn-save"
            disabled={disabled}
          >
            {mutation.isPending ? 'Publishing...' : 'Publish changes'}
            {mutation.isPending && <ButtonSpinnerIcon />}
          </button>
        </div>
        {!isValid && isDirty && (
          <Message messageStyle="error" title="Please review your form" />
        )}
        {mutation.isError && (
          <Message
            messageStyle="error"
            title="Error publishing changes"
            message="Please review your form"
          />
        )}
        {mutation.isSuccess && (
          <Message messageStyle="success" title="Successfully published" />
        )}
      </div>
    </form>
  );
};

export default AuthorForm;
